import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { createLot } from '../../services/Lot/lotService';
import ErrorModal from '../Modals/ErrorModal';
import SuccessModal from '../Modals/SuccessModal';
import InputField from '../Utils/InputField';
import PersonInput from '../Utils/PersonInput';
import ProductInput from '../Utils/ProductInput';
import HarvestInput from '../Utils/HarvestInput';
import FarmInput from '../Utils/FarmInput';
import SiloInput from '../Utils/SiloInput';
import CurrencyInput from '../Utils/CurrencyInput';
import DateInput from '../Utils/DateInput';
import { convertDateToISO } from '../Utils/utils';

const CreateLotModal = ({ show, handleClose, refreshLots, onCreated }) => {
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const { register, handleSubmit, formState: { errors }, reset, control, setValue, trigger } = useForm({
    defaultValues: {
      creditValue: 0,
      deliveredValue: 0,
      balance: 0,
    },
  });
  
  const onSubmit = async (data) => {
    try {
      const lotData = {
        ...data,
        personId: data.personId?.id,
        productId: data.productId?.id,
        harvestId: data.harvestId?.id,
        farmId: data.farmId?.id,
        siloId: data.siloId?.id,
        contractAt: data.contractAt ? convertDateToISO(data.contractAt) : null,
      };
      const created = await createLot(lotData);

      if (onCreated) {
        onCreated({
          id: created.uuid,
          description: created.name,
        });
      } else {
        setSuccessMessage('Lote criado com sucesso!');
        setShowSuccessModal(true);
        setValue('personId', { name: '', id: null });
      }

      reset();
    } catch (error) {
      setErrorMessage(error.message || 'Erro ao criar Lote.');
      setShowErrorModal(true);
    }
  };

  const handleCloseErrorModal = () => setShowErrorModal(false);
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    refreshLots();
    handleClose();
  };

  useEffect(() => {
    if (show) {
      reset();
    }
  }, [show, reset]);

  useEffect(() => {
    setValue('creditValue', 0);
    setValue('deliveredValue', 0);
    setValue('balance', 0);
  }, [setValue]);

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Cadastrar Lote</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.stopPropagation();
              handleSubmit(onSubmit)(e);
            }}
          >
            <Row>
              <Col md={6}>
                <InputField
                  label="Descrição"
                  name="description"
                  placeholder="Digita a Descrição"
                  register={register}
                  errors={errors}
                  rules={{ required: 'Descrição é obrigatória' }}
                />
              </Col>
              <Col md={3}>
                <CurrencyInput
                  label="Valor do Crédito"
                  name="creditValue"
                  placeholder="Digite o valor"
                  register={register}
                  setValue={setValue}
                  trigger={trigger}
                  errors={errors}
                  rules={{ required: 'O valor é obrigatório' }}
                />
              </Col>
              <Col md={3}>
                <CurrencyInput
                  label="Valor Entregue"
                  name="deliveredValue"
                  placeholder="Digite o valor"
                  register={register}
                  setValue={setValue}
                  trigger={trigger}
                  errors={errors}
                  rules={{ required: 'O valor é obrigatório' }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <CurrencyInput
                  label="Saldo"
                  name="balance"
                  placeholder="Digite o valor"
                  register={register}
                  setValue={setValue}
                  trigger={trigger}
                  errors={errors}
                  rules={{ required: 'O valor é obrigatório' }}
                />
              </Col>
              <Col md={3}>
                <Controller
                  name="contractAt"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DateInput
                      isEditing = { false }
                      label="Data do Contrato"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error}
                      placeholder="__/__/____"
                    />
                  )}
                  rules={{
                    required: 'Data é obrigatória',
                  }}
                />
              </Col>
              <Col md={6}>
                <InputField
                  label="Observações"
                  name="observations"
                  placeholder="Digita as observações"
                  register={register}
                  errors={errors}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Controller
                  name="personId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <PersonInput
                      value={field.value}
                      onChange={(selectedHarvest) => setValue('personId', selectedHarvest)}
                      label="Cliente"
                      isInvalid={!!fieldState.error}
                      createdNew={true}
                      type={['isCustomer', 'isProducer', 'isSupplier']}
                    />
                  )}
                />
              </Col>
              <Col md={6}>
                <Controller
                  name="productId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <ProductInput
                      value={field.value}
                      onChange={(selectedHarvest) => setValue('productId', selectedHarvest)}
                      label="Produto"
                      isInvalid={!!fieldState.error}
                      createdNew={true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Controller
                  name="harvestId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <HarvestInput
                      value={field.value}
                      onChange={(selectedHarvest) => setValue('harvestId', selectedHarvest)}
                      label="Safra"
                      isInvalid={!!fieldState.error}
                      createdNew={true}
                    />
                  )}
                />
              </Col>
              <Col md={6}>
                <Controller
                  name="farmId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FarmInput
                      value={field.value}
                      onChange={(selectedHarvest) => setValue('farmId', selectedHarvest)}
                      label="Fazenda"
                      isInvalid={!!fieldState.error}
                      createdNew={true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Controller
                  name="siloId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <SiloInput
                      value={field.value}
                      onChange={(selectedHarvest) => setValue('siloId', selectedHarvest)}
                      label="Silo"
                      isInvalid={!!fieldState.error}
                      createdNew={true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Button variant="primary" className="button-custom w-100 mt-3" type="submit">
              Cadastrar
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <ErrorModal
        show={showErrorModal}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        successMessage={successMessage}
      />
    </>
  );
};

export default CreateLotModal;